import { useState } from "react";
import {
  ArrowRight,
  Boxes,
  Building2,
  CheckCircle2,
  ChevronRight,
  Cpu,
  Factory,
  FileCheck2,
  MapPin,
  Package,
  PackageCheck,
  Palette,
  Plane,
  Send,
  Ship,
  Truck,
  type LucideIcon,
} from "lucide-react";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { RevealSection } from "@/components/Reveal";
import { SiteLayout } from "@/components/SiteLayout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { LogisticsGlobe } from "@/components/solutions/LogisticsGlobe";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";

export function LogisticsPage() {
  const { messages, site, href } = useSite();
  const [globeReady, setGlobeReady] = useState(false);
  const salesCta = localizeUrl("/contacts?inquiry=sales#inquiry-form", site.locale, href);
  const distributors = localizeUrl("/distributors", site.locale, href);
  const customization = localizeUrl("/solutions/customization", site.locale, href);
  const solutions = localizeUrl("/solutions", site.locale, href);
  const products = localizeUrl("/products", site.locale, href);
  const about = localizeUrl("/about", site.locale, href);

  const heroStages = [
    { icon: Factory, label: messages.logisticsHeroStage1 },
    { icon: Package, label: messages.logisticsHeroStage2 },
    { icon: Ship, label: messages.logisticsHeroStage3 },
    { icon: FileCheck2, label: messages.logisticsHeroStage4 },
  ];

  const stats = [
    { value: messages.logisticsStat1Value, label: messages.logisticsStat1Label },
    { value: messages.logisticsStat2Value, label: messages.logisticsStat2Label },
    { value: messages.logisticsStat3Value, label: messages.logisticsStat3Label },
    { value: messages.logisticsStat4Value, label: messages.logisticsStat4Label },
  ];

  const makeCards: { icon: LucideIcon; title: string; tag: string; body: string }[] = [
    {
      icon: Factory,
      title: messages.logisticsMakeCard1Title,
      tag: messages.logisticsMakeCard1Tag,
      body: messages.logisticsMakeCard1Body,
    },
    {
      icon: Boxes,
      title: messages.logisticsMakeCard2Title,
      tag: messages.logisticsMakeCard2Tag,
      body: messages.logisticsMakeCard2Body,
    },
    {
      icon: Cpu,
      title: messages.logisticsMakeCard3Title,
      tag: messages.logisticsMakeCard3Tag,
      body: messages.logisticsMakeCard3Body,
    },
  ];

  const flowSteps: { icon: LucideIcon; title: string; body: string }[] = [
    {
      icon: Factory,
      title: messages.logisticsFlowStep1Title,
      body: messages.logisticsFlowStep1Body,
    },
    {
      icon: Package,
      title: messages.logisticsFlowStep2Title,
      body: messages.logisticsFlowStep2Body,
    },
    {
      icon: FileCheck2,
      title: messages.logisticsFlowStep3Title,
      body: messages.logisticsFlowStep3Body,
    },
    {
      icon: PackageCheck,
      title: messages.logisticsFlowStep4Title,
      body: messages.logisticsFlowStep4Body,
    },
  ];

  const modes: { icon: LucideIcon; title: string; body: string; sampleOnly?: boolean }[] = [
    { icon: Plane, title: messages.logisticsModeAirTitle, body: messages.logisticsModeAirBody },
    { icon: Ship, title: messages.logisticsModeSeaTitle, body: messages.logisticsModeSeaBody },
    { icon: Truck, title: messages.logisticsModeRoadTitle, body: messages.logisticsModeRoadBody },
    {
      icon: Send,
      title: messages.logisticsModeCourierTitle,
      body: messages.logisticsModeCourierBody,
      sampleOnly: true,
    },
  ];

  const handleItems = [
    messages.logisticsHandleItem1,
    messages.logisticsHandleItem2,
    messages.logisticsHandleItem3,
    messages.logisticsHandleItem4,
    messages.logisticsHandleItem5,
    messages.logisticsHandleItem6,
    messages.logisticsHandleItem7,
    messages.logisticsHandleItem8,
    messages.logisticsHandleItem9,
  ];

  const links: { icon: LucideIcon; title: string; body: string; href: string }[] = [
    {
      icon: MapPin,
      title: messages.logisticsLinkDistributorsTitle,
      body: messages.logisticsLinkDistributorsBody,
      href: distributors,
    },
    {
      icon: Palette,
      title: messages.logisticsLinkCustomizationTitle,
      body: messages.logisticsLinkCustomizationBody,
      href: customization,
    },
    {
      icon: Boxes,
      title: messages.logisticsLinkProductsTitle,
      body: messages.logisticsLinkProductsBody,
      href: products,
    },
    {
      icon: Building2,
      title: messages.logisticsLinkAboutTitle,
      body: messages.logisticsLinkAboutBody,
      href: about,
    },
  ];

  const faqs = [
    { q: messages.logisticsFaqQ1, a: messages.logisticsFaqA1 },
    { q: messages.logisticsFaqQ2, a: messages.logisticsFaqA2 },
    { q: messages.logisticsFaqQ3, a: messages.logisticsFaqA3 },
    { q: messages.logisticsFaqQ4, a: messages.logisticsFaqA4 },
    { q: messages.logisticsFaqQ5, a: messages.logisticsFaqA5 },
    { q: messages.logisticsFaqQ6, a: messages.logisticsFaqA6 },
  ];

  return (
    <SiteLayout>
      <PageBreadcrumb
        items={[
          { label: messages.navSolutions, href: solutions },
          { label: messages.logisticsBreadcrumb },
        ]}
      />

      {/* Hero — logistics control tower */}
      <section
        data-logistics-page
        className="logi-control-hero relative isolate overflow-hidden border-b border-border/60"
      >
        <div
          aria-hidden
          className="bg-grid pointer-events-none absolute inset-0 -z-20 opacity-40"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-32 -top-40 -z-20 h-[760px] w-[900px] rounded-full opacity-65 blur-3xl"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <svg
          aria-hidden
          viewBox="0 0 1440 720"
          preserveAspectRatio="none"
          className="logi-hero-route-field pointer-events-none absolute inset-0 -z-10 hidden h-full w-full lg:block"
        >
          <path d="M630 180 C840 120 950 170 1180 90" />
          <path d="M560 330 C820 270 960 360 1370 245" />
          <path d="M620 470 C870 410 1030 510 1430 430" />
        </svg>

        <div className="relative mx-auto max-w-7xl px-6 pb-10 pt-14 sm:pt-18 lg:min-h-[42rem] lg:pb-40 lg:pt-20">
          <div className="relative z-20 mx-auto max-w-2xl text-center lg:pointer-events-none lg:select-none lg:mx-0 lg:max-w-[43rem] lg:text-left">
            <div className="inline-flex animate-fade-up items-center gap-2 rounded-full border border-primary/20 bg-background/75 px-3 py-1.5 text-xs font-medium text-foreground shadow-[var(--shadow-soft)] backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
              {messages.logisticsHeroEyebrow}
            </div>
            <h1
              className="mt-7 text-balance text-4xl font-semibold leading-[0.98] tracking-[-0.035em] animate-fade-up sm:text-6xl lg:text-7xl"
            >
              {messages.logisticsHeroTitle}{" "}
              <span className="text-gradient">{messages.logisticsHeroTitleAccent}</span>
            </h1>
            <p
              className="mx-auto mt-7 max-w-xl text-pretty text-base leading-relaxed text-muted-foreground animate-fade-up sm:text-lg lg:mx-0"
              style={{ animationDelay: "160ms" }}
            >
              {messages.logisticsHeroSub}
            </p>
            <div
              className="mt-8 flex flex-col items-center gap-4 animate-fade-up sm:flex-row sm:justify-center lg:pointer-events-auto lg:select-auto lg:justify-start"
              style={{ animationDelay: "240ms" }}
            >
              <Button asChild size="lg" className="w-full animate-glow sm:w-auto">
                <a href={salesCta}>
                  {messages.logisticsHeroCtaPrimary}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </a>
              </Button>
              <a
                href={distributors}
                className="group inline-flex items-center gap-2 px-2 py-2 text-sm font-medium text-foreground transition-colors hover:text-primary"
              >
                {messages.logisticsHeroCtaSecondary}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </div>

          <div
            className={`logi-hero-globe relative z-10 -mx-[20%] mt-8 w-[140%] transition-all duration-1000 sm:-mx-[14%] sm:w-[128%] lg:absolute lg:-right-56 lg:-top-28 lg:mx-0 lg:mt-0 lg:w-[66rem] ${
              globeReady
                ? "translate-y-0 scale-100 opacity-100"
                : "pointer-events-none translate-y-6 scale-[0.985] opacity-0"
            }`}
          >
            <LogisticsGlobe
              ariaLabel={messages.logisticsGlobeCaption}
              onReady={() => setGlobeReady(true)}
            />
          </div>

          <ol className="logi-control-strip relative z-30 mt-8 grid grid-cols-2 overflow-hidden rounded-2xl border border-primary/15 bg-background/80 shadow-[var(--shadow-elevated)] backdrop-blur-xl animate-fade-up sm:mt-10 sm:grid-cols-4 lg:absolute lg:bottom-8 lg:left-6 lg:right-6 lg:mt-0">
            {heroStages.map(({ icon: Icon, label }, index) => (
              <li
                key={label}
                className="logi-control-stage relative flex min-h-24 items-center gap-3 px-4 py-4 sm:px-5"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/10">
                  <Icon className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-primary/60">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="mt-1 block text-sm font-semibold text-foreground">{label}</span>
                </span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Coverage stats */}
      <RevealSection className="border-b border-border/60 section-tint py-12">
        <div className="mx-auto max-w-7xl px-6">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-8 md:grid-cols-4">
            {stats.map((s, i) => (
              <div
                key={s.label}
                className={`rounded-xl border border-border/50 bg-background/50 p-4 text-center shadow-[var(--shadow-soft)] md:border-y-0 md:border-r-0 md:bg-transparent md:p-0 md:text-left md:shadow-none ${
                  i > 0 ? "md:border-l md:border-border/60 md:pl-6" : "md:border-0"
                }`}
              >
                <dt className="text-2xl font-semibold tracking-tight sm:text-3xl">
                  <span className="text-gradient">{s.value}</span>
                </dt>
                <dd className="mx-auto mt-2 max-w-[15rem] text-sm leading-relaxed text-muted-foreground md:mx-0">
                  {s.label}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </RevealSection>

      {/* Manufacturing footprint */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.logisticsMakeEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.logisticsMakeTitle}
            </h2>
            <p className="mt-4 text-base text-muted-foreground">{messages.logisticsMakeBody}</p>
          </div>

          <ul className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
            {makeCards.map(({ icon: Icon, title, tag, body }) => (
              <li
                key={title}
                className="group relative overflow-hidden rounded-2xl border border-border/60 bg-card/70 p-6 shadow-[var(--shadow-soft)] backdrop-blur transition-all duration-500 hover:-translate-y-1 hover:border-primary/30 hover:shadow-[var(--shadow-elevated)]"
              >
                <span
                  aria-hidden
                  className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                />
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-foreground/90">
                    {tag}
                  </span>
                </div>
                <h3 className="mt-5 inline-flex items-center gap-1.5 text-lg font-semibold tracking-tight text-foreground">
                  <MapPin className="h-4 w-4 text-primary" />
                  {title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </li>
            ))}
          </ul>
        </div>
      </RevealSection>

      {/* How your order reaches you */}
      <RevealSection className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.logisticsFlowEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.logisticsFlowTitle}
            </h2>
            <p className="mt-4 text-base text-muted-foreground">{messages.logisticsFlowBody}</p>
          </div>

          <ol className="relative mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <span
              aria-hidden
              className="absolute left-[12.5%] right-[12.5%] top-[2.15rem] hidden h-px bg-gradient-to-r from-primary/15 via-primary/50 to-primary/15 lg:block"
            />
            {flowSteps.map(({ icon: Icon, title, body }, index) => (
              <li
                key={title}
                className="group relative rounded-2xl border border-border/60 bg-card/85 p-6 shadow-[var(--shadow-soft)] backdrop-blur transition-all duration-500 hover:-translate-y-1 hover:border-primary/30 hover:shadow-[var(--shadow-elevated)]"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="absolute right-5 top-5 text-2xl font-semibold tabular-nums text-primary/25">
                  {index + 1}
                </span>
                {index < flowSteps.length - 1 && (
                  <ChevronRight
                    aria-hidden
                    className="absolute -right-2.5 top-1/2 z-10 hidden h-5 w-5 -translate-y-1/2 rounded-full bg-background text-primary/50 lg:block"
                  />
                )}
                <h3 className="mt-5 text-base font-semibold text-foreground">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </li>
            ))}
          </ol>
        </div>
      </RevealSection>

      {/* Shipping modes + handled-for-you */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-primary">
                {messages.logisticsModesEyebrow}
              </p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
                {messages.logisticsModesTitle}
              </h2>
              <p className="mt-4 text-base text-muted-foreground">{messages.logisticsModesBody}</p>

              <div className="mt-6 flex gap-3 rounded-2xl border border-primary/20 bg-primary/[0.05] p-4 shadow-[var(--shadow-soft)]">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Building2 className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    {messages.logisticsB2bNoteTitle}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    {messages.logisticsB2bNoteBody}
                  </p>
                </div>
              </div>

              <ul className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {modes.map(({ icon: Icon, title, body, sampleOnly }) => (
                  <li
                    key={title}
                    className={`group rounded-2xl border p-5 shadow-[var(--shadow-soft)] backdrop-blur transition-all duration-500 hover:-translate-y-1 hover:shadow-[var(--shadow-elevated)] ${
                      sampleOnly
                        ? "border-primary/25 bg-primary/[0.05] hover:border-primary/40"
                        : "border-border/60 bg-card/70 hover:border-primary/30"
                    }`}
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-4 text-sm font-semibold text-foreground">{title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{body}</p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative overflow-hidden rounded-3xl border border-primary/20 bg-primary/[0.04] p-6 shadow-[var(--shadow-elevated)] sm:p-8">
              <div
                aria-hidden
                className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-primary/10 blur-3xl"
              />
              <div className="relative">
                <p className="text-xs font-medium uppercase tracking-wider text-primary">
                  {messages.logisticsHandleEyebrow}
                </p>
                <h2 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">
                  {messages.logisticsHandleTitle}
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                  {messages.logisticsHandleBody}
                </p>
                <ul className="mt-6 space-y-3">
                  {handleItems.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-foreground/90">
                      <CheckCircle2 className="ota-accent mt-0.5 h-4.5 w-4.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 flex items-start gap-2.5 border-t border-primary/15 pt-5 text-sm font-medium text-foreground/90">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  {messages.logisticsHandleFootnote}
                </p>
              </div>
            </div>
          </div>
        </div>
      </RevealSection>

      {/* Cross-links */}
      <RevealSection className="border-b border-border/60 section-tint py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.logisticsLinksEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.logisticsLinksTitle}
            </h2>
          </div>

          <ul className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {links.map(({ icon: Icon, title, body, href: linkHref }) => (
              <li key={title}>
                <a
                  href={linkHref}
                  className="group flex h-full flex-col rounded-2xl border border-border/60 bg-card/70 p-6 shadow-[var(--shadow-soft)] backdrop-blur transition-all duration-500 hover:-translate-y-1 hover:border-primary/30 hover:shadow-glow"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 text-primary ring-1 ring-primary/10 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 text-base font-semibold tracking-tight text-foreground">
                    {title}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                    {body}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary">
                    {messages.logisticsLinkCta}
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </RevealSection>

      {/* FAQ */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto grid max-w-6xl gap-8 px-6 lg:grid-cols-[0.7fr_1.3fr] lg:gap-16">
          <div className="text-center lg:text-left">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.logisticsFaqEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.logisticsFaqTitle}
            </h2>
          </div>
          <Accordion
            type="single"
            collapsible
            className="rounded-2xl border border-border/60 bg-card/60 px-5 shadow-[var(--shadow-soft)] backdrop-blur sm:px-7"
          >
            {faqs.map(({ q, a }, i) => (
              <AccordionItem key={i} value={`q${i}`}>
                <AccordionTrigger className="text-base font-medium">{q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </RevealSection>

      {/* Final CTA */}
      <RevealSection className="relative overflow-hidden bg-foreground py-16 text-background sm:py-20">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-0 opacity-30"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <p className="text-xs font-medium uppercase tracking-wider text-background/70">
            {messages.logisticsFinalEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.logisticsFinalTitle}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-background/80">
            {messages.logisticsFinalBody}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto">
              <a href={salesCta}>{messages.logisticsFinalCta}</a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="w-full border-background/40 bg-transparent text-background hover:bg-background/10 hover:text-background sm:w-auto"
            >
              <a href={distributors}>{messages.logisticsFinalCtaSecondary}</a>
            </Button>
          </div>
        </div>
      </RevealSection>
    </SiteLayout>
  );
}
